/**
 * IndexedDBによるチャンク永続化機能
 * 録音チャンクを即座にローカルに保存し、アップロード失敗時もデータを保護
 */

const DB_NAME = 'OtoeigoRecorder'
const DB_VERSION = 1
const STORE_NAME = 'recordingChunks'

export class IndexedDBStorage {
  constructor() {
    this.db = null
    this.isInitialized = false
  }
  
  /**
   * IndexedDB初期化
   */
  async init() {
    if (this.isInitialized) {
      return this.db
    }
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      
      request.onerror = () => {
        console.error('❌ IndexedDB初期化エラー:', request.error)
        reject(request.error)
      }
      
      request.onsuccess = () => {
        this.db = request.result
        this.isInitialized = true
        console.log('✅ IndexedDB初期化完了')
        resolve(this.db)
      }
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        
        // ストア作成
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
          
          // インデックス作成
          store.createIndex('recordingId', 'recordingId', { unique: false })
          store.createIndex('chunkIndex', 'chunkIndex', { unique: false })
          store.createIndex('uploaded', 'uploaded', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          
          console.log('✅ IndexedDBストア作成完了')
        }
      }
    })
  }
  
  /**
   * チャンク保存
   */
  async saveChunk(chunkData, chunkInfo) {
    await this.init()
    
    // BlobをArrayBufferに変換してIndexedDBに保存
    let dataArrayBuffer = null
    if (chunkData instanceof Blob) {
      dataArrayBuffer = await chunkData.arrayBuffer()
    } else {
      dataArrayBuffer = chunkData
    }
    
    // chunkInfoからProxyオブジェクトを完全に除去して安全にシリアライズ
    const safeChunkInfo = {
      recordingId: String(chunkInfo.recordingId || ''),
      chunkIndex: Number(chunkInfo.chunkIndex || 0),
      timestamp: Number(chunkInfo.timestamp || Date.now()),
      day: chunkInfo.day ? (typeof chunkInfo.day === 'object' ? String(chunkInfo.day.value || chunkInfo.day) : String(chunkInfo.day)) : null,
      type: chunkInfo.type ? (typeof chunkInfo.type === 'object' ? String(chunkInfo.type.value || chunkInfo.type) : String(chunkInfo.type)) : null,
      userId: chunkInfo.userId ? (typeof chunkInfo.userId === 'object' ? String(chunkInfo.userId.value || chunkInfo.userId) : String(chunkInfo.userId)) : null,
      size: Number(chunkInfo.size || 0),
      mimeType: String(chunkInfo.mimeType || 'audio/webm')
    }
    
    // デバッグログ：元のchunkInfoの詳細
    console.log('🔍 元のchunkInfo:', {
      recordingId: chunkInfo.recordingId,
      chunkIndex: chunkInfo.chunkIndex,
      timestamp: chunkInfo.timestamp,
      day: chunkInfo.day,
      type: chunkInfo.type,
      userId: chunkInfo.userId,
      size: chunkInfo.size,
      mimeType: chunkInfo.mimeType,
      dayType: typeof chunkInfo.day,
      typeType: typeof chunkInfo.type,
      userIdType: typeof chunkInfo.userId
    })
    
    // デバッグログ：安全化後のchunkInfo
    console.log('🔍 安全化後のchunkInfo:', safeChunkInfo)
    
    // 完全にシリアライズ可能なオブジェクトを作成
    const chunkRecord = {
      id: `${safeChunkInfo.recordingId}_${safeChunkInfo.chunkIndex}`,
      recordingId: String(safeChunkInfo.recordingId),
      chunkIndex: Number(safeChunkInfo.chunkIndex),
      timestamp: Number(safeChunkInfo.timestamp),
      day: safeChunkInfo.day ? String(safeChunkInfo.day) : null,
      type: safeChunkInfo.type ? String(safeChunkInfo.type) : null,
      userId: safeChunkInfo.userId ? String(safeChunkInfo.userId) : null,
      size: Number(safeChunkInfo.size),
      mimeType: String(safeChunkInfo.mimeType),
      data: dataArrayBuffer,
      uploaded: Boolean(false),
      uploadAttempts: Number(0),
      lastUploadAttempt: null,
      createdAt: Number(Date.now())
    }
    
    // デバッグログ
    console.log('🔍 保存前のchunkRecord:', {
      id: chunkRecord.id,
      recordingId: chunkRecord.recordingId,
      chunkIndex: chunkRecord.chunkIndex,
      day: chunkRecord.day,
      type: chunkRecord.type,
      userId: chunkRecord.userId,
      size: chunkRecord.size,
      mimeType: chunkRecord.mimeType,
      dataType: chunkRecord.data ? chunkRecord.data.constructor.name : 'null',
      uploaded: chunkRecord.uploaded,
      uploadAttempts: chunkRecord.uploadAttempts,
      lastUploadAttempt: chunkRecord.lastUploadAttempt,
      createdAt: chunkRecord.createdAt
    })
    
    return new Promise((resolve, reject) => {
      try {
        // オブジェクトの深いコピーを作成してProxyオブジェクトを完全に除去（dataはArrayBufferのため除外）
        const serializedRecord = JSON.parse(JSON.stringify({
          id: chunkRecord.id,
          recordingId: chunkRecord.recordingId,
          chunkIndex: chunkRecord.chunkIndex,
          timestamp: chunkRecord.timestamp,
          day: chunkRecord.day,
          type: chunkRecord.type,
          userId: chunkRecord.userId,
          size: chunkRecord.size,
          mimeType: chunkRecord.mimeType,
          uploaded: chunkRecord.uploaded,
          uploadAttempts: chunkRecord.uploadAttempts,
          lastUploadAttempt: chunkRecord.lastUploadAttempt,
          createdAt: chunkRecord.createdAt
        }))

        // dataはArrayBufferのためJSON化されない。必ず元のchunkRecord.dataを渡す
        const finalRecord = {
          id: String(serializedRecord.id),
          recordingId: String(serializedRecord.recordingId),
          chunkIndex: Number(serializedRecord.chunkIndex),
          timestamp: Number(serializedRecord.timestamp),
          day: serializedRecord.day ? String(serializedRecord.day) : null,
          type: serializedRecord.type ? String(serializedRecord.type) : null,
          userId: serializedRecord.userId ? String(serializedRecord.userId) : null,
          size: Number(serializedRecord.size),
          mimeType: String(serializedRecord.mimeType),
          data: chunkRecord.data,
          uploaded: Boolean(serializedRecord.uploaded),
          uploadAttempts: Number(serializedRecord.uploadAttempts),
          lastUploadAttempt: serializedRecord.lastUploadAttempt,
          createdAt: Number(serializedRecord.createdAt)
        }
        
        console.log('🔍 最終的な保存オブジェクト:', finalRecord)
        
        const transaction = this.db.transaction([STORE_NAME], 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.put(finalRecord)
      
      request.onsuccess = () => {
        console.log('💾 チャンク保存完了:', {
          recordingId: chunkInfo.recordingId,
          chunkIndex: chunkInfo.chunkIndex,
          size: chunkInfo.size
        })
        resolve(chunkRecord)
      }
      
      request.onerror = () => {
        console.error('❌ チャンク保存エラー:', request.error)
        // QuotaExceededError などストレージ不足もここに入ってくる
        reject(request.error)
      }
      
      } catch (error) {
        console.error('❌ シリアライズエラー:', error)
        reject(error)
      }
    })
  }
  
  /**
   * 録音IDに紐づく全チャンク取得
   */
  async getChunksByRecordingId(recordingId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('recordingId')
      const request = index.getAll(recordingId)
      
      request.onsuccess = () => {
        const chunks = request.result.sort((a, b) => a.chunkIndex - b.chunkIndex)
        
        // ArrayBufferをBlobに変換
        const processedChunks = chunks.map(chunk => ({
          ...chunk,
          data: new Blob([chunk.data], { type: chunk.mimeType || 'audio/webm' })
        }))
        
        console.log(`📦 チャンク取得完了: ${processedChunks.length}個`, { recordingId })
        resolve(processedChunks)
      }
      
      request.onerror = () => {
        console.error('❌ チャンク取得エラー:', request.error)
        reject(request.error)
      }
    })
  }
  
  /**
   * アップロード済みのチャンクを取得（特定の録音ID）
   */
  async getUploadedChunks(recordingId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('recordingId')
      const request = index.getAll(recordingId)
      
      request.onsuccess = () => {
        const chunks = request.result
        const uploadedChunks = chunks.filter(chunk => chunk.uploaded === true)
        console.log(`📊 アップロード済みチャンク取得: ${uploadedChunks.length}個 (録音ID: ${recordingId})`)
        resolve(uploadedChunks)
      }
      
      request.onerror = () => {
        console.error('❌ アップロード済みチャンク取得エラー:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * 未アップロードチャンク取得
   */
  async getUnuploadedChunks() {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()
      
      request.onsuccess = () => {
        console.log('🔍 全チャンクデータ:', request.result.length, '個')
        
        // 未アップロードのチャンクをフィルタリング（厳密な検証）
        const chunks = request.result.filter(chunk => {
          // 基本的な存在確認
          if (!chunk || typeof chunk !== 'object') {
            return false
          }
          
          // recordingIdの存在確認
          if (!chunk.recordingId || typeof chunk.recordingId !== 'string') {
            return false
          }
          
          // uploadedフラグの確認
          if (typeof chunk.uploaded !== 'boolean' || chunk.uploaded === true) {
            return false
          }
          
          // chunkIndexの確認
          if (typeof chunk.chunkIndex !== 'number') {
            return false
          }
          
          return true
        }).map(chunk => ({
          ...chunk,
          data: new Blob([chunk.data], { type: chunk.mimeType || 'audio/webm' })
        }))
        
        // ソート（安全な処理）
        chunks.sort((a, b) => {
          // recordingIdでソート
          if (a.recordingId !== b.recordingId) {
            return a.recordingId.localeCompare(b.recordingId)
          }
          
          // chunkIndexでソート
          return a.chunkIndex - b.chunkIndex
        })
        
        console.log(`📤 有効な未アップロードチャンク: ${chunks.length}個`)
        resolve(chunks)
      }
      
      request.onerror = () => {
        console.error('❌ 未アップロードチャンク取得エラー:', request.error)
        reject(request.error)
      }
    })
  }
  
  /**
   * IndexedDBをクリア（デバッグ用）
   */
  async clearAllChunks() {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()
      
      request.onsuccess = () => {
        console.log('🗑️ IndexedDBクリア完了')
        resolve()
      }
      
      request.onerror = () => {
        console.error('❌ IndexedDBクリアエラー:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * チャンクのアップロード状態更新
   */
  async markChunkAsUploaded(chunkId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const getRequest = store.get(chunkId)
      
      getRequest.onsuccess = () => {
        const chunk = getRequest.result
        if (chunk) {
          chunk.uploaded = true
          chunk.uploadedAt = Date.now()
          
          const putRequest = store.put(chunk)
          putRequest.onsuccess = () => {
            console.log('✅ チャンクアップロード完了マーク:', chunkId)
            resolve(chunk)
          }
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          reject(new Error('チャンクが見つかりません'))
        }
      }
      
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  /**
   * アップロード成功時にチャンクを削除
   */
  async deleteUploadedChunk(chunkId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const deleteRequest = store.delete(chunkId)
      
      deleteRequest.onsuccess = () => {
        console.log('🗑️ アップロード済みチャンク削除:', chunkId)
        resolve()
      }
      
      deleteRequest.onerror = () => {
        console.error('❌ チャンク削除エラー:', deleteRequest.error)
        reject(deleteRequest.error)
      }
    })
  }
  
  /**
   * アップロード試行回数更新
   */
  async updateUploadAttempts(chunkId, success = false) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const getRequest = store.get(chunkId)
      
      getRequest.onsuccess = () => {
        const chunk = getRequest.result
        if (chunk) {
          chunk.uploadAttempts++
          chunk.lastUploadAttempt = Date.now()
          if (success) {
            chunk.uploaded = true
            chunk.uploadedAt = Date.now()
          }
          
          const putRequest = store.put(chunk)
          putRequest.onsuccess = () => {
            console.log('📊 アップロード試行回数更新:', {
              chunkId,
              attempts: chunk.uploadAttempts,
              success
            })
            resolve(chunk)
          }
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          reject(new Error('チャンクが見つかりません'))
        }
      }
      
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  /**
   * チャンクを失敗としてマーク（再取得を防ぐ）
   */
  async markChunkAsFailed(chunkId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const getRequest = store.get(chunkId)
      
      getRequest.onsuccess = () => {
        const chunk = getRequest.result
        if (chunk) {
          chunk.uploaded = true // 失敗でもuploaded: trueにして再取得を防ぐ
          chunk.failed = true
          chunk.failedAt = Date.now()
          
          const putRequest = store.put(chunk)
          putRequest.onsuccess = () => {
            console.log('❌ チャンク失敗マーク:', chunkId)
            resolve(chunk)
          }
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          reject(new Error('チャンクが見つかりません'))
        }
      }
      
      getRequest.onerror = () => reject(getRequest.error)
    })
  }
  
  /**
   * 録音完了後のクリーンアップ
   */
  async cleanupRecording(recordingId) {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('recordingId')
      const request = index.getAll(recordingId)
      
      request.onsuccess = () => {
        const chunks = request.result
        let deletedCount = 0
        
        if (chunks.length === 0) {
          resolve(0)
          return
        }
        
        const deletePromises = chunks.map(chunk => {
          return new Promise((resolveDelete, rejectDelete) => {
            const deleteRequest = store.delete(chunk.id)
            deleteRequest.onsuccess = () => {
              deletedCount++
              resolveDelete()
            }
            deleteRequest.onerror = () => rejectDelete(deleteRequest.error)
          })
        })
        
        Promise.all(deletePromises)
          .then(() => {
            console.log(`🗑️ 録音クリーンアップ完了: ${deletedCount}個のチャンクを削除`, { recordingId })
            resolve(deletedCount)
          })
          .catch(reject)
      }
      
      request.onerror = () => reject(request.error)
    })
  }
  
  /**
   * ストレージ使用量取得
   */
  async getStorageUsage() {
    await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()
      
      request.onsuccess = () => {
        const chunks = request.result
        const totalSize = chunks.reduce((sum, chunk) => sum + (chunk.size || 0), 0)
        const uploadedCount = chunks.filter(chunk => chunk.uploaded).length
        const failedCount = chunks.filter(chunk => chunk.failed).length
        const pendingCount = chunks.length - uploadedCount - failedCount
        
        resolve({
          totalChunks: chunks.length,
          totalSize: totalSize,
          uploadedCount,
          failedCount,
          pendingCount,
          totalSizeMB: Math.round(totalSize / 1024 / 1024 * 100) / 100
        })
      }
      
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * ストレージ容量チェック
   */
  async checkStorageCapacity() {
    try {
      const usage = await this.getStorageUsage()
      const maxSizeMB = 100 // 100MB制限
      
      if (usage.totalSizeMB > maxSizeMB) {
        console.warn(`⚠️ ストレージ容量不足: ${usage.totalSizeMB}MB / ${maxSizeMB}MB`)
        return {
          isFull: true,
          currentSize: usage.totalSizeMB,
          maxSize: maxSizeMB,
          message: `ストレージ容量が不足しています (${usage.totalSizeMB}MB / ${maxSizeMB}MB)`
        }
      }
      
      return {
        isFull: false,
        currentSize: usage.totalSizeMB,
        maxSize: maxSizeMB,
        message: `ストレージ使用量: ${usage.totalSizeMB}MB / ${maxSizeMB}MB`
      }
    } catch (error) {
      console.error('❌ ストレージ容量チェックエラー:', error)
      return {
        isFull: false,
        currentSize: 0,
        maxSize: 100,
        message: 'ストレージ容量を確認できませんでした'
      }
    }
  }

  /**
   * 古いデータの自動クリーンアップ（指定日数以上前のデータを削除）
   */
  async cleanupOldData(daysToKeep = 7) {
    await this.init()
    
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000)
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()
      
      request.onsuccess = () => {
        const chunks = request.result
        const oldChunks = chunks.filter(chunk => 
          chunk.createdAt && chunk.createdAt < cutoffTime
        )
        
        if (oldChunks.length === 0) {
          console.log('🧹 古いデータなし')
          resolve(0)
          return
        }
        
        let deletedCount = 0
        const deletePromises = oldChunks.map(chunk => {
          return new Promise((resolveDelete, rejectDelete) => {
            const deleteRequest = store.delete(chunk.id)
            deleteRequest.onsuccess = () => {
              deletedCount++
              resolveDelete()
            }
            deleteRequest.onerror = () => rejectDelete(deleteRequest.error)
          })
        })
        
        Promise.all(deletePromises)
          .then(() => {
            console.log(`🧹 古いデータクリーンアップ完了: ${deletedCount}個のチャンクを削除`)
            resolve(deletedCount)
          })
          .catch(reject)
      }
      
      request.onerror = () => {
        console.error('❌ 古いデータ取得エラー:', request.error)
        reject(request.error)
      }
    })
  }
}
